/**
 * Route Access Configuration
 *
 * Single source of truth for brand-context route access control.
 * Used by BrandGuard (route protection), Sidebar (nav filtering),
 * and AuthContext (switchBrand redirect).
 *
 * Supports 3 brand types: GROUP, POINT_OF_SALE, CENTRAL
 */

import type { BrandType } from '@bstock/shared';

export type BrandAccess = 'all' | 'groupOnly' | 'siteOnly' | 'siteAndCentral' | 'centralOnly';

/**
 * Map of route prefixes to their brand access level.
 * - groupOnly: accessible only for GROUP brands
 * - siteOnly: accessible only for POINT_OF_SALE brands
 * - siteAndCentral: accessible for POINT_OF_SALE and CENTRAL brands
 * - centralOnly: accessible only for CENTRAL brands
 * - all (default): accessible for all brand types
 */
export const ROUTE_ACCESS: Record<string, BrandAccess> = {
  '/': 'siteAndCentral',
  '/products': 'groupOnly',
  '/recettes': 'groupOnly',
  '/achats': 'siteAndCentral',
  '/controles/ventes-pos': 'siteOnly',
  '/stock': 'siteAndCentral',
  '/rapports/marge': 'siteOnly',
  '/rapports': 'siteAndCentral',
};

/**
 * Check if a pathname is accessible for the given brand type.
 */
export function isRouteAccessible(pathname: string, brandType: BrandType | null): boolean {
  if (!brandType) return false;

  for (const [prefix, access] of Object.entries(ROUTE_ACCESS)) {
    const matches = prefix === '/' ? pathname === '/' : pathname.startsWith(prefix);
    if (matches) {
      if (access === 'groupOnly' && brandType !== 'GROUP') return false;
      if (access === 'siteOnly' && brandType !== 'POINT_OF_SALE') return false;
      if (access === 'centralOnly' && brandType !== 'CENTRAL') return false;
      if (access === 'siteAndCentral' && brandType === 'GROUP') return false;
    }
  }
  return true;
}

/**
 * Legacy overload: Check if a pathname is accessible using isGroup boolean.
 * Kept for backward compatibility during migration.
 */
export function isRouteAccessibleLegacy(pathname: string, isGroup: boolean): boolean {
  return isRouteAccessible(pathname, isGroup ? 'GROUP' : 'POINT_OF_SALE');
}

/**
 * Get the brand access level for a given path.
 * Matches against ROUTE_ACCESS prefixes.
 */
export function getRouteAccess(path: string): BrandAccess {
  for (const [prefix, access] of Object.entries(ROUTE_ACCESS)) {
    const matches = prefix === '/' ? path === '/' : path.startsWith(prefix);
    if (matches) return access;
  }
  return 'all';
}

/**
 * Check if a given route access level is allowed for a brand type.
 */
export function isAccessAllowed(access: BrandAccess, brandType: BrandType): boolean {
  if (access === 'all') return true;
  if (access === 'groupOnly') return brandType === 'GROUP';
  if (access === 'siteOnly') return brandType === 'POINT_OF_SALE';
  if (access === 'centralOnly') return brandType === 'CENTRAL';
  if (access === 'siteAndCentral') return brandType !== 'GROUP';
  return true;
}

/**
 * Get the default landing page for the current brand type.
 */
export function getDefaultRoute(brandTypeOrIsGroup: BrandType | boolean): string {
  if (typeof brandTypeOrIsGroup === 'boolean') {
    return brandTypeOrIsGroup ? '/products/raw' : '/';
  }
  return brandTypeOrIsGroup === 'GROUP' ? '/products/raw' : '/';
}
